<template>
    <v-container class="mt-10 pb-12" style="min-width: 1200px; max-width: 1600px; padding-left: 24px; padding-right: 24px;">
        <!-- Header Section -->
        <div class="leave-header mb-6">
            <div class="d-flex align-center justify-space-between mb-4">
                <div>
                    <h1 class="text-h4 font-weight-bold mb-2">Leave Management</h1>
                    <p class="text-subtitle-1 grey--text mb-0">View and manage employee leave balances</p>
                </div>
                <div class="d-flex align-center" v-if="isSuperAdmin && mainTab === 1">
                    <v-select
                        v-model="selectedYear"
                        :items="availableYears"
                        label="Year"
                        outlined
                        dense
                        hide-details
                        style="max-width: 120px;"
                        class="mr-4"
                    ></v-select>
                    <v-btn outlined color="primary" @click="exportCSV" class="text-none">
                        <v-icon left small>mdi-download</v-icon>
                        Export CSV
                    </v-btn>
                </div>
            </div>

        </div>

        <!-- Resolving access -->
        <div v-if="!accessResolved" class="text-center py-12">
            <v-progress-circular indeterminate color="primary" size="40" />
        </div>

        <!-- No access -->
        <v-card v-else-if="!canAccess" outlined class="pa-10 text-center">
            <v-icon size="56" color="grey lighten-1">mdi-lock-outline</v-icon>
            <p class="text-h6 grey--text mt-4 mb-1">You don't have access to Leave Management</p>
            <p class="text-body-2 grey--text mb-0">
                This page is available to managers and administrators.
            </p>
        </v-card>

        <!-- Tabs Navigation -->
        <v-tabs v-if="accessResolved && canAccess" v-model="mainTab" class="mb-6" background-color="transparent" color="primary">
            <v-tab>
                <v-icon left small>mdi-view-week</v-icon>
                Wallchart
            </v-tab>
            <v-tab v-if="isSuperAdmin">
                <v-icon left small>mdi-account-multiple</v-icon>
                Leave Balances
            </v-tab>
            <v-tab>
                <v-icon left small>{{ isSuperAdmin ? 'mdi-calendar-clock' : 'mdi-calendar-check' }}</v-icon>
                {{ isSuperAdmin ? 'Leave Requests Overview' : 'Leave Approvals' }}
            </v-tab>
        </v-tabs>

        <!-- Loading State -->
        <v-skeleton-loader v-if="accessResolved && canAccess && loading" type="card, card, card"></v-skeleton-loader>

        <!-- Tabs Content -->
        <v-tabs-items v-if="accessResolved && canAccess && !loading" v-model="mainTab">
            <!-- Tab 0: Wallchart -->
            <v-tab-item>
                <LeaveWallchart
                    :requests="leaveRequests"
                    :users="scopedUsers"
                    :leave-types="laeveTypes"
                    :balances="leaveBalances()"
                    :roles="get_roles"
                    :managed-team-ids="managerTeamIds"
                    :role-managers="roleManagers ? roleManagers() : []"
                    :current-user-id="get_user && get_user.id"
                />
            </v-tab-item>

            <!-- Tab 1: Leave Balances (Super Admin only) -->
            <v-tab-item v-if="isSuperAdmin">
                <!-- Search Bar -->
                <v-text-field
                    v-model="searchQuery"
                    placeholder="Search employees..."
                    outlined
                    dense
                    hide-details
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    class="mb-6"
                    style="max-width: 400px;"
                />

                <!-- Statistics Cards -->
                <v-row class="mb-6">
                    <v-col cols="12" sm="6" md="4">
                        <v-card class="pa-4 position-relative" elevation="1" style="background-color: #FEF2F2;">
                            <div class="d-flex align-center mb-2">
                                <v-icon class="mr-2" color="error">mdi-alert-circle</v-icon>
                                <span class="text-caption text-uppercase font-weight-bold">Need Review</span>
                            </div>
                            <div class="text-h3 font-weight-bold">{{ needReviewCount }}</div>
                            <div class="text-caption grey--text">(>12 days)</div>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-card class="pa-4 position-relative" elevation="1" style="background-color: #EFF6FF;">
                            <div class="d-flex align-center mb-2">
                                <v-icon class="mr-2" color="primary">mdi-chart-line</v-icon>
                                <span class="text-caption text-uppercase font-weight-bold">Average Balance</span>
                            </div>
                            <div class="text-h3 font-weight-bold">{{ averageBalance.toFixed(2) }}</div>
                            <div class="text-caption grey--text">(days)</div>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-card class="pa-4 position-relative" elevation="1" style="background-color: #F0FDF4;">
                            <div class="d-flex align-center mb-2">
                                <v-icon class="mr-2" color="success">mdi-chart-arc</v-icon>
                                <span class="text-caption text-uppercase font-weight-bold">Total Balance</span>
                            </div>
                            <div class="text-h3 font-weight-bold">{{ totalBalance.toFixed(2) }}</div>
                            <div class="text-caption grey--text">(days)</div>
                        </v-card>
                    </v-col>
                </v-row>

        <div class="employee-leave-list">
            <div v-for="department in filteredDepartments" :key="department.name" class="department-section mb-4">
                <v-card elevation="1" class="department-card">
                    <!-- Department Header -->
                    <div class="department-header pa-4" style="background-color: #F8F9FA; cursor: pointer;" @click="toggleDepartment(department.name)">
                        <div class="d-flex align-center justify-space-between">
                            <div class="d-flex align-center">
                                <v-icon small class="mr-2">{{ isDepartmentExpanded(department.name) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                                <div :style="{ width: '4px', height: '24px', backgroundColor: department.color, marginRight: '12px' }"></div>
                                <span class="text-h6 font-weight-bold">{{ department.name }}</span>
                                <v-chip small class="ml-2">{{ department.employees.length }}</v-chip>
                            </div>
                        </div>
                    </div>

                    <!-- Collapsible Content -->
                    <v-expand-transition>
                        <div v-show="isDepartmentExpanded(department.name)">
                            <!-- Table Header -->
                            <div class="leave-table-header pa-4">
                                <div class="leave-col leave-employee">Employee</div>
                                <div class="leave-col leave-balance">Balance for {{ selectedYear }}</div>
                                <div class="leave-col leave-entitlement">Entitlement for {{ selectedYear }}</div>
                                <div class="leave-col leave-type">Type</div>
                                <div class="leave-col leave-actions">Actions</div>
                            </div>

                            <!-- Employee Rows -->
                            <div v-for="employee in department.employees" :key="employee.id" class="employee-item">
                        <div class="employee-row pa-4" :class="{ 'expanded': expandedEmployee === employee.id }" @click="toggleEmployeeExpand(employee.id)">
                            <div class="leave-col leave-employee">
                                <div class="d-flex align-center">
                                    <v-icon small class="mr-2">{{ expandedEmployee === employee.id ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                                    <v-avatar size="32" color="primary" class="mr-3">
                                        <span class="white--text text-caption">{{ getInitials(employee.name) }}</span>
                                    </v-avatar>
                                    <span class="font-weight-medium">{{ employee.name }}</span>
                                </div>
                            </div>
                            <div class="leave-col leave-balance">
                                <span class="font-weight-bold">{{ employee.balance.toFixed(2) }}</span>
                            </div>
                            <div class="leave-col leave-entitlement">
                                <span>{{ employee.entitlement }}</span>
                            </div>
                            <div class="leave-col leave-type">
                                <v-chip x-small color="primary" outlined>{{ employee.type }}</v-chip>
                            </div>
                            <div class="leave-col leave-actions" @click.stop>
                                <v-btn x-small color="primary" outlined @click="openAssignCreditsDialog(employee)">
                                    <v-icon x-small left>mdi-pencil</v-icon>
                                    Edit Balance
                                </v-btn>
                            </div>
                        </div>

                        <!-- Expanded Leave History Section -->
                        <v-expand-transition>
                            <div v-if="expandedEmployee === employee.id" class="leave-history-section pa-4" style="background-color: #F8F9FA;">
                                <div class="d-flex align-center mb-4">
                                    <v-icon color="primary" class="mr-2">mdi-history</v-icon>
                                    <span class="text-subtitle-1 font-weight-bold">Leave History</span>
                                </div>

                                <v-tabs v-model="historyTab" background-color="transparent" color="primary" class="mb-4">
                                    <!-- <v-tab>
                                        <v-icon small left>mdi-plus-circle</v-icon>
                                        Accruals ({{ employee.history.accruals.length }})
                                    </v-tab> -->
                                    <v-tab>
                                        <v-icon small left>mdi-minus-circle</v-icon>
                                        Usage ({{ employee.history.usage.length }})
                                    </v-tab>
                                    <v-tab>
                                        <v-icon small left>mdi-pencil</v-icon>
                                        Edits ({{ employee.history.edits.length }})
                                    </v-tab>
                                </v-tabs>

                                <v-tabs-items v-model="historyTab">
                                    <!-- Accruals Tab -->
                                    <!-- <v-tab-item>
                                        <v-card flat>
                                            <v-card-text v-if="employee.history.accruals.length === 0" class="text-center grey--text">
                                                No accrual history yet
                                            </v-card-text>
                                            <v-simple-table v-else dense>
                                                <template v-slot:default>
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Amount</th>
                                                            <th>Type</th>
                                                            <th>Notes</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="accrual in employee.history.accruals" :key="accrual.id">
                                                            <td>{{ formatDate(accrual.date) }}</td>
                                                            <td><span class="success--text font-weight-bold">+{{ accrual.amount }}</span></td>
                                                            <td>{{ accrual.type }}</td>
                                                            <td>{{ accrual.notes }}</td>
                                                        </tr>
                                                    </tbody>
                                                </template>
                                            </v-simple-table>
                                        </v-card>
                                    </v-tab-item> -->

                                    <!-- Usage Tab -->
                                    <v-tab-item>
                                        <v-card flat>
                                            <v-card-text v-if="employee.history.usage.length === 0" class="text-center grey--text">
                                                No usage history yet
                                            </v-card-text>
                                            <v-simple-table v-else dense>
                                                <template v-slot:default>
                                                    <thead>
                                                        <tr>
                                                            <th>Date Range</th>
                                                            <th>Amount</th>
                                                            <th>Type</th>
                                                            <th>Status</th>
                                                            <th>Approver</th>
                                                            <th>Notes</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="usage in employee.history.usage" :key="usage.id">
                                                            <td>
                                                                {{ formatDate(usage.startDate) }}
                                                                <span v-if="usage.startDate !== usage.endDate"> - {{ formatDate(usage.endDate) }}</span>
                                                                <span v-if="usage.isHalfDay" class="text-caption grey--text ml-1">({{ usage.halfDayPeriod }})</span>
                                                            </td>
                                                            <td>
                                                                <span class="error--text font-weight-bold">-{{ usage.amount }}</span>
                                                                <span v-if="usage.isHalfDay" class="text-caption grey--text ml-1">day</span>
                                                            </td>
                                                            <td>
                                                                <v-chip x-small outlined>{{ usage.leaveType }}</v-chip>
                                                            </td>
                                                            <td>
                                                                <v-chip x-small :color="getStatusColor(usage.status)">{{ usage.status }}</v-chip>
                                                            </td>
                                                            <td>{{ usage.approver }}</td>
                                                            <td>{{ usage.notes || '-' }}</td>
                                                        </tr>
                                                    </tbody>
                                                </template>
                                            </v-simple-table>
                                        </v-card>
                                    </v-tab-item>

                                    <!-- Edits Tab -->
                                    <v-tab-item>
                                        <v-card flat>
                                            <v-card-text v-if="employee.history.edits.length === 0" class="text-center grey--text">
                                                No edits history yet
                                            </v-card-text>
                                            <v-simple-table v-else dense>
                                                <template v-slot:default>
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Field</th>
                                                            <th>Old Value</th>
                                                            <th>New Value</th>
                                                            <th>Edited By</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="edit in employee.history.edits" :key="edit.id">
                                                            <td>{{ formatDate(edit.date) }}</td>
                                                            <td>{{ edit.field }}</td>
                                                            <td>{{ edit.oldValue }}</td>
                                                            <td>{{ edit.newValue }}</td>
                                                            <td>{{ edit.editedBy }}</td>
                                                        </tr>
                                                    </tbody>
                                                </template>
                                            </v-simple-table>
                                        </v-card>
                                    </v-tab-item>
                                </v-tabs-items>
                            </div>
                        </v-expand-transition>
                            </div>
                        </div>
                    </v-expand-transition>
                </v-card>
            </div>
        </div>
            </v-tab-item>

            <!-- Tab 2: Leave Requests Overview (Super Admin) / Leave Approvals (managers) -->
            <v-tab-item>
                <LeaveApproval v-if="!isSuperAdmin" mode="all" />
                <template v-else>
                <!-- Statistics Cards for Leave Requests -->
                <v-row class="mb-6">
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="pa-4 position-relative" elevation="1" style="background-color: #FFF7ED;">
                            <div class="d-flex align-center mb-2">
                                <v-icon class="mr-2" color="orange">mdi-clock-outline</v-icon>
                                <span class="text-caption text-uppercase font-weight-bold">Pending</span>
                            </div>
                            <div class="text-h3 font-weight-bold">{{ pendingRequestsCount }}</div>
                            <div class="text-caption grey--text">Avg: {{ averagePendingDays.toFixed(1) }} days</div>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="pa-4 position-relative" elevation="1" style="background-color: #F0FDF4;">
                            <div class="d-flex align-center mb-2">
                                <v-icon class="mr-2" color="green">mdi-check-circle</v-icon>
                                <span class="text-caption text-uppercase font-weight-bold">Approved</span>
                            </div>
                            <div class="text-h3 font-weight-bold">{{ approvedRequestsCount }}</div>
                            <div class="text-caption grey--text">Avg: {{ averageApprovedDays.toFixed(1) }} days</div>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="pa-4 position-relative" elevation="1" style="background-color: #FEF2F2;">
                            <div class="d-flex align-center mb-2">
                                <v-icon class="mr-2" color="red">mdi-close-circle</v-icon>
                                <span class="text-caption text-uppercase font-weight-bold">Declined</span>
                            </div>
                            <div class="text-h3 font-weight-bold">{{ declinedRequestsCount }}</div>
                            <div class="text-caption grey--text">Avg: {{ averageDeclinedDays.toFixed(1) }} days</div>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="pa-4 position-relative" elevation="1" style="background-color: #F5F3FF;">
                            <div class="d-flex align-center mb-2">
                                <v-icon class="mr-2" color="purple">mdi-calendar-multiple</v-icon>
                                <span class="text-caption text-uppercase font-weight-bold">Total Requests</span>
                            </div>
                            <div class="text-h3 font-weight-bold">{{ leaveRequests.length }}</div>
                            <div class="text-caption grey--text">Avg: {{ averageAllRequestsDays.toFixed(1) }} days</div>
                        </v-card>
                    </v-col>
                </v-row>

                <v-card elevation="1">
                    <v-card-title class="d-flex align-center">
                        <span class="text-h6 font-weight-bold">
                            <v-icon color="primary" class="mr-2">mdi-calendar-multiple</v-icon>
                            All Leave Requests
                        </span>
                        <v-spacer></v-spacer>
                        <v-chip small class="pa-2" outlined>
                            <span style="font-weight: 600; font-size: 0.8rem">{{ filteredLeaveRequests.length }} requests</span>
                        </v-chip>
                    </v-card-title>
                    <v-card-text>
                        <v-row class="mb-4">
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="leaveRequestsSearch"
                                    outlined
                                    dense
                                    append-icon="mdi-magnify"
                                    placeholder="Search by employee name or notes..."
                                    clearable
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select
                                    v-model="leaveRequestsFilter"
                                    :items="filterOptions"
                                    item-text="text"
                                    item-value="value"
                                    outlined
                                    dense
                                    label="Filter by status"
                                    prepend-inner-icon="mdi-filter"
                                ></v-select>
                            </v-col>
                        </v-row>

                        <div class="overview-scroll-container" style="height: calc(70vh - 100px); overflow-y: auto;">
                            <div v-if="filteredLeaveRequests.length === 0" class="text-center py-8">
                                <v-icon size="64" color="grey lighten-1">mdi-calendar-remove</v-icon>
                                <p class="text-h6 grey--text mt-4">No leave requests found</p>
                            </div>
                            <div
                                v-for="item in filteredLeaveRequests"
                                :key="item.id"
                                class="overview-request-item mb-3"
                            >
                                <v-list-item class="custom-request-item">
                                    <v-list-item-content>
                                        <v-row no-gutters>
                                            <v-col cols="12" md="4" class="mb-2">
                                                <v-list-item-title class="mb-1">
                                                    <span style="font-weight: 700; font-size: 0.95rem; color: #1976d2;">{{ item.user?.name || 'Unknown' }}</span>
                                                </v-list-item-title>
                                                <v-list-item-subtitle>
                                                    <v-chip small dense label color="primary" text-color="white" class="mb-1">
                                                        {{ item.leave_type?.name || 'No Type' }}
                                                    </v-chip>
                                                    <div class="text-caption mt-1">
                                                        {{ formatRequestDatesDisplay(item) }}
                                                    </div>
                                                    <div v-if="item.leave_count" class="leave-credits-info mt-2">
                                                        <v-progress-linear
                                                            color="blue"
                                                            rounded
                                                            :value="(item.leave_count.used_leaves / item.leave_count.leave_count) * 100"
                                                            height="8"
                                                            class="mb-1"
                                                        ></v-progress-linear>
                                                        <span class="credits-text">
                                                            {{ item.leave_count.used_leaves }} / {{ item.leave_count.leave_count }} Used
                                                            <span class="ml-1" style="color: #1976d2;">
                                                                ({{ item.leave_count.leave_count - item.leave_count.used_leaves }} remaining)
                                                            </span>
                                                        </span>
                                                    </div>
                                                </v-list-item-subtitle>
                                            </v-col>
                                            <v-col cols="12" md="4" class="mb-2">
                                                <div class="leave-request-details">
                                                    <div class="duration-info mb-2">
                                                        <v-icon small color="orange" class="mr-1">mdi-calendar-clock</v-icon>
                                                        <span style="font-weight: 600; font-size: 0.9rem;">
                                                            {{ formatRequestDurationDisplay(item) }}
                                                        </span>
                                                    </div>
                                                    <div class="note-section-overview">
                                                        <v-icon small color="grey" class="mr-1">mdi-note-text</v-icon>
                                                        <div class="note-content-overview">
                                                            <span
                                                                :class="[
                                                                    'leave-note-overview',
                                                                    { 'expanded': expandedRequestNotes[item.id] }
                                                                ]"
                                                            >
                                                                {{ item.note || 'No notes provided' }}
                                                            </span>
                                                            <v-btn
                                                                v-if="shouldShowExpandButton(item.note)"
                                                                text
                                                                x-small
                                                                color="primary"
                                                                class="ml-1 pa-0"
                                                                style="min-width: auto; text-transform: none;"
                                                                @click="toggleRequestNoteExpansion(item.id)"
                                                            >
                                                                {{ expandedRequestNotes[item.id] ? 'Show less' : 'Show more' }}
                                                            </v-btn>
                                                        </div>
                                                    </div>
                                                </div>
                                            </v-col>
                                            <v-col cols="12" md="4" class="text-md-right">
                                                <div class="approval-info-overview">
                                                    <div class="mb-2">
                                                        <v-chip small dense label color="grey lighten-1" class="mb-1">
                                                            <v-icon small class="mr-1">mdi-account-check</v-icon>
                                                            {{ (item.approver?.first_name ? `${item.approver.first_name} ${item.approver.last_name || ''}`.trim() : item.approver?.name) || 'No approver' }}
                                                        </v-chip>
                                                    </div>
                                                    <div class="mb-2">
                                                        <v-chip
                                                            small
                                                            dense
                                                            label
                                                            :color="getRequestStatusColor(item.status?.id || item.leave_status_id)"
                                                            text-color="white"
                                                        >
                                                            <v-icon small class="mr-1">{{ getRequestStatusIcon(item.status?.id || item.leave_status_id) }}</v-icon>
                                                            {{ item.status?.name || getStatusName(item.leave_status_id) }}
                                                        </v-chip>
                                                    </div>
                                                    <div class="text-caption grey--text">
                                                        Requested: {{ formatDate(item.created_at) }}
                                                    </div>
                                                </div>
                                            </v-col>
                                        </v-row>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-divider></v-divider>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
                </template>
            </v-tab-item>
        </v-tabs-items>

        <!-- Assign Leave Credits Dialog -->
        <v-dialog v-model="assignCreditsDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title>
                    <span class="text-h6 font-weight-bold">Edit Leave Balance</span>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="closeAssignCreditsDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-6">
                    <div v-if="selectedEmployeeForCredits" class="mb-4">
                        <div class="d-flex align-center mb-4">
                            <v-avatar size="48" color="primary" class="mr-3">
                                <span class="white--text">{{ getInitials(selectedEmployeeForCredits.name) }}</span>
                            </v-avatar>
                            <div>
                                <div class="font-weight-bold">{{ selectedEmployeeForCredits.name }}</div>
                                <div class="text-caption grey--text">{{ selectedEmployeeForCredits.email }}</div>
                            </div>
                        </div>
                        <v-alert dense text type="info" class="mb-4">
                            Resulting balance for {{ assignCreditsYear }}: <strong>{{ dialogCurrentBalance.toFixed(2) }} days</strong>
                        </v-alert>
                    </div>

                    <v-select
                        v-model="assignCreditsYear"
                        :items="availableYears"
                        label="Year"
                        outlined
                        dense
                        class="mb-2"
                        :rules="[v => !!v || 'Year is required']"
                    ></v-select>

                    <v-select
                        v-model="selectedLeaveType"
                        :items="requestTypes"
                        item-text="name"
                        item-value="id"
                        label="Request Type"
                        outlined
                        dense
                        :rules="[v => !!v || 'Leave type is required']"
                    ></v-select>

                    <v-text-field
                        v-model.number="leaveCreditsAmount"
                        label="Leave credits"
                        outlined
                        dense
                        type="number"
                        min="0"
                        step="0.5"
                        :hint="`Edit the entitlement for this leave type in ${assignCreditsYear}`"
                        persistent-hint
                        class="mb-2"
                        :rules="[v => v >= 0 || 'Leave credits cannot be negative']"
                    ></v-text-field>

                    <v-text-field
                        v-model.number="usedLeavesAmount"
                        label="Used leaves"
                        outlined
                        dense
                        type="number"
                        min="0"
                        step="0.5"
                        hint="Manually correct the number of leaves already used. Use with care - this does not change any individual leave request."
                        persistent-hint
                        :rules="[v => v >= 0 || 'Used leaves cannot be negative']"
                    ></v-text-field>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeAssignCreditsDialog">
                        Cancel
                    </v-btn>
                    <v-btn
                        color="primary"
                        @click="assignLeaveCredits"
                        :disabled="!selectedLeaveType || leaveCreditsAmount < 0 || usedLeavesAmount < 0"
                        :loading="assigningCredits"
                    >
                        <v-icon left small>mdi-check</v-icon>
                        Save
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import LeaveWallchart from "./LeaveWallchart.vue";
import LeaveApproval from "../profile/LeaveApproval.vue";
import { canAccessLeaveManagement as resolveLeaveAccess, isLeaveSuperAdmin } from "./leaveUtils";

export default {
    name: "LeaveManagementComponent",
    components: { LeaveWallchart, LeaveApproval },
    inject: ['users', 'refreshUsers', 'leaveBalances', 'refreshLeaveBalances', 'isLoadingLeaveBalances', 'roleManagers'],
    data() {
        return {
            mainTab: 0, // 0 = Wallchart, 1 = Leave Balances, 2 = Leave Requests Overview
            searchQuery: '',
            expandedEmployee: null,
            historyTab: 0,
            loading: false,
            accessResolved: false, // false until we know whether this user has access
            expandedDepartments: [], // Track which departments are expanded
            selectedYear: new Date().getFullYear(), // Year the balances table is showing
            
            // Leave Requests Overview
            leaveRequestsSearch: '',
            leaveRequestsFilter: 'all', // all, pending, approved, declined
            expandedRequestNotes: {}, // Track expanded notes in requests overview
            leaveRequests: [], // Will be populated from API
            
            // Assign Credits Dialog
            assignCreditsDialog: false,
            selectedEmployeeForCredits: null,
            selectedLeaveType: null,
            leaveCreditsAmount: 0,
            usedLeavesAmount: 0,
            assigningCredits: false,
            assignCreditsYear: new Date().getFullYear(), // Year being edited in the dialog
            
            // Department color mapping
            departmentColors: {
                'Engineering': '#2196F3',
                'Management': '#4CAF50',
                'Design': '#FF9800',
                'Analytics': '#9C27B0',
                'Sales': '#F44336',
                'Marketing': '#E91E63',
                'HR': '#00BCD4',
                'Finance': '#009688',
                'Operations': '#FF5722',
                'Product': '#673AB7',
                'Other': '#9E9E9E'
            }
        };
    },
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user',
            get_roles: 'auth/get_roles',
            laeveTypes: 'leave/getLeaveTypes',
        }),
        
        requestTypes() {
            console.log('[requestTypes] laeveTypes:', this.laeveTypes);
            if (!this.laeveTypes || this.laeveTypes.length === 0) {
                return [];
            }
            return this.laeveTypes.filter((q) => {
                return q.id == 1 || q.id == 6;
            });
        },

        isSuperAdmin() {
            return isLeaveSuperAdmin(this.get_user);
        },

        canAccess() {
            return resolveLeaveAccess(this.get_user, this.roleManagers ? this.roleManagers() : []);
        },

        // Team ids the current user manages — null for Super Admin (see all teams).
        managerTeamIds() {
            if (this.isSuperAdmin) return null;
            const rm = this.roleManagers ? this.roleManagers() : [];
            return rm
                .filter((r) => r.manager_id === (this.get_user && this.get_user.id))
                .map((r) => r.user_role_id);
        },

        // Super Admin sees everyone; managers only see the users the API returned
        // leave data for (their team members + direct reports).
        scopedUsers() {
            const all = this.users() || [];
            if (this.isSuperAdmin) return all;
            const ids = new Set((this.leaveBalances() || []).map((u) => u.id));
            return all.filter((u) => ids.has(u.id));
        },

        // Filter users by search query
        filteredUsers() {
            let filtered = this.scopedUsers;
            
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                filtered = filtered.filter(user => {
                    const fullName = this.getUserFullName(user).toLowerCase();
                    const email = user.email?.toLowerCase() || '';
                    const position = user.position_name?.toLowerCase() || '';
                    const roleName = user.role?.name?.toLowerCase() || '';
                    
                    return fullName.includes(query) || 
                           email.includes(query) || 
                           position.includes(query) ||
                           roleName.includes(query);
                });
            }
            
            return filtered;
        },
        
        // Group users by role/department (same as user directory)
        groupedUsersByRole() {
            const grouped = {};
            
            this.filteredUsers.forEach(user => {
                // Group by role name or position
                const groupKey = user.role?.name || user.position_name || 'Other';
                
                if (!grouped[groupKey]) {
                    grouped[groupKey] = [];
                }
                grouped[groupKey].push(user);
            });
            
            return grouped;
            
        },
        
        // Convert grouped users to department structure
        filteredDepartments() {
            return Object.keys(this.groupedUsersByRole).map(roleName => {
                const employees = this.groupedUsersByRole[roleName].map(user => {
                    // Find leave data for this user from the API response
                    const userData = this.leaveBalances().find(u => u.id === user.id);

                    // Calculate total balance and entitlement from all leave types
                    let totalBalance = 0; // Remaining balance (leave_count - used_leaves)
                    let totalEntitlement = 0; // Total allocated
                    let totalUsed = 0; // Total used
                    let leaveTypes = [];

                    if (userData?.get_leaves && userData.get_leaves.length > 0) {
                        // Filter for the selected year only
                        const selectedYearLeaves = userData.get_leaves.filter(leave => leave.year === this.selectedYear);

                        selectedYearLeaves.forEach(leave => {
                            const entitlement = leave.leave_count || 0;
                            const used = leave.used_leaves || 0;
                            const remaining = entitlement - used;
                            
                            totalEntitlement += entitlement;
                            totalUsed += used;
                            totalBalance += remaining;
                            leaveTypes.push(leave.leave_type?.name || 'Unknown');
                        });
                    }
                    
                    // Parse leave requests into history
                    const leaveRequests = userData?.get_user_leave_requests || [];
                    
                    // Separate into accruals and usage
                    const usage = leaveRequests.map(request => ({
                        id: request.id,
                        startDate: request.from,
                        endDate: request.to,
                        amount: request.count,
                        status: request.status?.name || 'Unknown',
                        notes: request.note || '',
                        leaveType: request.leave_type?.name || 'Unknown',
                        isHalfDay: request.is_half_day,
                        halfDayPeriod: request.half_day_period,
                        approver: request.approver?.name || 'Unknown',
                        createdAt: request.created_at
                    }));
                    
                    // Parse leave edit logs
                    const editLogs = userData?.get_leave_edit_logs || [];
                    const edits = editLogs.map(log => {
                        // Find the editor user name
                        const editor = this.users().find(u => u.id === log.edited_by);
                        
                        // Start with the raw values
                        let oldValue = log.old_value || '-';
                        let newValue = log.new_value || '-';
                        
                        // If old or new leave type IDs exist, append leave type names
                        if (log.old_leave_type_id || log.new_leave_type_id) {
                            const oldLeaveType = this.laeveTypes.find(lt => lt.id === log.old_leave_type_id);
                            const newLeaveType = this.laeveTypes.find(lt => lt.id === log.new_leave_type_id);
                            
                            if (oldLeaveType && log.old_value) {
                                oldValue = `${log.old_value} (${oldLeaveType.name})`;
                            }
                            if (newLeaveType && log.new_value) {
                                newValue = `${log.new_value} (${newLeaveType.name})`;
                            }
                        }
                        
                        return {
                            id: log.id,
                            date: log.created_at,
                            field: this.formatFieldName(log.field),
                            oldValue: oldValue,
                            newValue: newValue,
                            editedBy: editor?.name || 'Unknown'
                        };
                    });
                    
                    return {
                        id: user.id,
                        name: this.getUserFullName(user),
                        email: user.email,
                        balance: totalBalance, // Remaining balance (entitlement - used)
                        entitlement: totalEntitlement, // Total allocated (0 if not assigned)
                        used: totalUsed, // Total used leaves
                        type: leaveTypes.length > 0 ? leaveTypes.join(', ') : 'Not Assigned',
                        history: {
                            accruals: [], // Can be populated later if backend provides accrual history
                            usage: usage,
                            edits: edits
                        },
                        userData: user, // Keep reference to original user data
                        rawLeaveData: userData // Keep reference to raw leave data for debugging
                    };
                });
                
                return {
                    name: roleName,
                    color: this.departmentColors[roleName] || this.departmentColors['Other'],
                    employees: employees
                };
            });
        },
        
        allEmployees() {
            return this.filteredDepartments.flatMap(dept => dept.employees);
        },
        
        needReviewCount() {
            return this.allEmployees.filter(emp => emp.balance > 12).length;
        },
        
        averageBalance() {
            if (this.allEmployees.length === 0) return 0;
            const total = this.allEmployees.reduce((sum, emp) => sum + emp.balance, 0);
            return total / this.allEmployees.length;
        },
        
        totalBalance() {
            return this.allEmployees.reduce((sum, emp) => sum + emp.balance, 0);
        },
        
        // Leave Requests Overview computed properties
        filterOptions() {
            return [
                { text: 'All Requests', value: 'all' },
                { text: 'Pending', value: 'pending' },
                { text: 'Approved', value: 'approved' },
                { text: 'Declined', value: 'declined' }
            ];
        },
        
        pendingRequestsCount() {
            return this.leaveRequests.filter(r => (r.status?.id || r.leave_status_id) === 1).length;
        },
        
        approvedRequestsCount() {
            return this.leaveRequests.filter(r => (r.status?.id || r.leave_status_id) === 2).length;
        },
        
        declinedRequestsCount() {
            return this.leaveRequests.filter(r => (r.status?.id || r.leave_status_id) === 3).length;
        },
        
        averagePendingDays() {
            const pending = this.leaveRequests.filter(r => (r.status?.id || r.leave_status_id) === 1);
            if (pending.length === 0) return 0;
            const total = pending.reduce((sum, r) => sum + (r.count || 0), 0);
            return total / pending.length;
        },
        
        averageApprovedDays() {
            const approved = this.leaveRequests.filter(r => (r.status?.id || r.leave_status_id) === 2);
            if (approved.length === 0) return 0;
            const total = approved.reduce((sum, r) => sum + (r.count || 0), 0);
            return total / approved.length;
        },
        
        averageDeclinedDays() {
            const declined = this.leaveRequests.filter(r => (r.status?.id || r.leave_status_id) === 3);
            if (declined.length === 0) return 0;
            const total = declined.reduce((sum, r) => sum + (r.count || 0), 0);
            return total / declined.length;
        },
        
        averageAllRequestsDays() {
            if (this.leaveRequests.length === 0) return 0;
            const total = this.leaveRequests.reduce((sum, r) => sum + (r.count || 0), 0);
            return total / this.leaveRequests.length;
        },
        
        filteredLeaveRequests() {
            let filtered = this.leaveRequests;
            
            // Filter by status
            if (this.leaveRequestsFilter !== 'all') {
                filtered = filtered.filter(request => {
                    const statusId = request.status?.id || request.leave_status_id;
                    if (this.leaveRequestsFilter === 'pending') return statusId === 1;
                    if (this.leaveRequestsFilter === 'approved') return statusId === 2;
                    if (this.leaveRequestsFilter === 'declined') return statusId === 3;
                    return true;
                });
            }
            
            // Filter by search query
            if (this.leaveRequestsSearch) {
                const query = this.leaveRequestsSearch.toLowerCase();
                filtered = filtered.filter(request => {
                    const userName = request.user?.name?.toLowerCase() || '';
                    const note = request.note?.toLowerCase() || '';
                    return userName.includes(query) || note.includes(query);
                });
            }
            
            return filtered.sort((a, b) => b.id - a.id);
        },

        // Years selectable in the year switcher / assign credits dialog:
        // a small range around the current year, plus any year that actually has data
        availableYears() {
            const years = new Set();
            const thisYear = new Date().getFullYear();
            for (let y = thisYear - 3; y <= thisYear + 1; y++) years.add(y);

            (this.leaveBalances() || []).forEach(user => {
                (user.get_leaves || []).forEach(leave => years.add(leave.year));
            });

            return Array.from(years).sort((a, b) => b - a);
        },

        dialogCurrentBalance() {
            return (this.leaveCreditsAmount || 0) - (this.usedLeavesAmount || 0);
        }
    },
    watch: {
        assignCreditsYear() {
            this.syncLeaveCreditsAmount();
        },
        selectedLeaveType() {
            this.syncLeaveCreditsAmount();
        },
        // roleManagers loads async in DefaultLayout, so a team-manager's access
        // may only resolve after mount — load the page data once it does.
        canAccess(allowed) {
            if (allowed) {
                this.accessResolved = true;
                this.initLeaveData();
            }
        }
    },
    mounted() {
        if (this.canAccess) {
            this.accessResolved = true;
            this.initLeaveData();
        } else {
            // give roleManagers time to load before showing the "no access" card
            this._accessTimer = setTimeout(() => { this.accessResolved = true; }, 3000);
        }
    },
    beforeDestroy() {
        if (this._accessTimer) clearTimeout(this._accessTimer);
    },
    methods: {
        async initLeaveData() {
            if (this._leaveDataStarted) return;
            this._leaveDataStarted = true;
            await this.loadData();
            await this.getLeaveTypes();
            await this.loadLeaveRequestsOverview();
        },
        async loadData() {
            try {
                this.loading = true;
                await Promise.all([
                    this.refreshUsers(),
                    this.refreshLeaveBalances()
                ]);
                console.log('[LeaveManagement] Users loaded:', this.users().length);
                console.log('[LeaveManagement] Leave balances loaded:', this.leaveBalances().length);
            } catch (error) {
                console.error('[LeaveManagement] Error loading data:', error);
                this.$toast.error('Failed to load data');
            } finally {
                this.loading = false;
            }
        },
        
        async loadLeaveRequestsOverview() {
            try {
                const response = await this.$axios.get('/api/leave/overview');
                if (response.data) {
                    this.leaveRequests = response.data;
                    console.log('[LeaveManagement] Leave requests loaded:', this.leaveRequests.length);
                }
            } catch (error) {
                console.error('[LeaveManagement] Error loading leave requests:', error);
            }
        },
        
        async getLeaveTypes() {
            try {
                const response = await this.$axios.get('/api/leave/type');
                const { data } = response;
                this.$store.commit('leave/SET_LEAVE_TYPES', data);
            } catch (error) {
                console.error('[LeaveManagement] Error fetching leave types - service layer', error);
            }
        },
        
        getUserFullName(user) {
            if (!user) return 'Unknown User';
            if (user.first_name || user.last_name) {
                return `${user.first_name || ''} ${user.last_name || ''}`.trim();
            }
            return user.name || user.email || 'Unknown User';
        },
        
        getInitials(name) {
            if (!name) return '?';
            return name
                .split(' ')
                .map(word => word[0])
                .join('')
                .toUpperCase()
                .substring(0, 2);
        },
        
        toggleEmployeeExpand(employeeId) {
            if (this.expandedEmployee === employeeId) {
                this.expandedEmployee = null;
            } else {
                this.expandedEmployee = employeeId;
                this.historyTab = 0; // Reset to first tab
            }
        },
        
        toggleDepartment(departmentName) {
            const index = this.expandedDepartments.indexOf(departmentName);
            if (index > -1) {
                this.expandedDepartments.splice(index, 1);
            } else {
                this.expandedDepartments.push(departmentName);
            }
        },
        
        isDepartmentExpanded(departmentName) {
            return this.expandedDepartments.includes(departmentName);
        },
        
        formatDate(dateString) {
            if (!dateString) return '';
            return this.$date(dateString).format('MMM DD, YYYY');
        },
        
        getStatusColor(status) {
            const colors = {
                'Approved': 'success',
                'Request': 'warning',
                'Pending': 'warning',
                'Rejected': 'error',
                'Cancelled': 'grey',
                'Denied': 'error'
            };
            return colors[status] || 'primary';
        },
        
        // Find this employee's leave_user_counts record for a given year + leave type
        findLeaveRecordForYearAndType(employee, year, leaveTypeId) {
            const leaves = employee?.rawLeaveData?.get_leaves || [];
            return leaves.find(l => l.year === year && l.leave_type_id === leaveTypeId) || null;
        },

        // Re-sync the leave credits/used leaves fields to match the record for the
        // currently selected year/leave type in the dialog (0 if none exists yet)
        syncLeaveCreditsAmount() {
            if (!this.assignCreditsDialog || !this.selectedEmployeeForCredits) return;
            const record = this.findLeaveRecordForYearAndType(
                this.selectedEmployeeForCredits,
                this.assignCreditsYear,
                this.selectedLeaveType
            );
            this.leaveCreditsAmount = record ? record.leave_count : 0;
            this.usedLeavesAmount = record ? (record.used_leaves || 0) : 0;
        },

        openAssignCreditsDialog(employee) {
            this.selectedEmployeeForCredits = employee;
            this.assignCreditsYear = this.selectedYear;

            // Try to pre-select the employee's leave type for the selected year, if they have one
            const existingLeave = (employee.rawLeaveData?.get_leaves || []).find(l => l.year === this.assignCreditsYear);
            this.selectedLeaveType = existingLeave?.leave_type_id || null;

            this.assignCreditsDialog = true;
            this.syncLeaveCreditsAmount();
        },

        closeAssignCreditsDialog() {
            this.assignCreditsDialog = false;
            this.selectedEmployeeForCredits = null;
            this.selectedLeaveType = null;
            this.leaveCreditsAmount = 0;
            this.usedLeavesAmount = 0;
        },

        async assignLeaveCredits() {
            if (!this.selectedLeaveType || this.leaveCreditsAmount < 0 || this.usedLeavesAmount < 0) {
                this.$toast.error('Please fill all required fields');
                return;
            }

            try {
                this.assigningCredits = true;

                const payload = {
                    user_id: this.selectedEmployeeForCredits.userData.id,
                    leave_type_id: this.selectedLeaveType,
                    leave_count: this.leaveCreditsAmount,
                    used_leaves: this.usedLeavesAmount,
                    year: this.assignCreditsYear
                };

                // If a record already exists for this user/type/year, target it directly
                // by id so the backend updates it rather than relying on a lookup match
                const existingRecord = this.findLeaveRecordForYearAndType(
                    this.selectedEmployeeForCredits,
                    this.assignCreditsYear,
                    this.selectedLeaveType
                );
                if (existingRecord) {
                    payload.id = existingRecord.id;
                }

                await this.$axios.post('/api/leave/credits', payload);

                this.$toast.success('Leave balance updated successfully');
                this.closeAssignCreditsDialog();
                // Refresh leave balances
                await this.refreshLeaveBalances();
            } catch (error) {
                console.error('[AssignCredits] Error:', error);
                this.$toast.error('Failed to update leave balance');
            } finally {
                this.assigningCredits = false;
            }
        },
        
        exportCSV() {
            const csvData = [];
            csvData.push(['Department', 'Employee', 'Email', 'Balance', 'Entitlement', 'Type', 'Used Leaves'].join(','));
            
            this.filteredDepartments.forEach(dept => {
                dept.employees.forEach(emp => {
                    const usedLeaves = emp.entitlement - emp.balance;
                    csvData.push([
                        dept.name,
                        emp.name,
                        emp.email,
                        emp.balance.toFixed(2),
                        emp.entitlement,
                        emp.type,
                        usedLeaves.toFixed(2)
                    ].join(','));
                });
            });
            
            const blob = new Blob([csvData.join('\n')], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `leave-management-${this.$date().format('YYYY-MM-DD')}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
            
            this.$toast.success('CSV exported successfully');
        },
        
        // Leave Requests Overview Methods
        formatRequestDatesDisplay(request) {
            const fromDate = this.$date(request.from).format('MMM D, YYYY');
            const toDate = this.$date(request.to).format('MMM D, YYYY');
            
            if (request.is_half_day) {
                const period = request.half_day_period ? ` (${request.half_day_period})` : '';
                return `${fromDate}${period}`;
            } else {
                if (request.from === request.to) {
                    return fromDate;
                } else {
                    return `${fromDate} - ${toDate}`;
                }
            }
        },
        
        formatRequestDurationDisplay(request) {
            if (request.is_half_day) {
                const period = request.half_day_period || 'N/A';
                return `0.5 days (${period === 'AM' ? 'Morning' : 'Afternoon'})`;
            } else {
                const days = request.count;
                return `${days} day${days > 1 ? 's' : ''}`;
            }
        },
        
        getRequestStatusColor(statusId) {
            switch (statusId) {
                case 1: return 'orange'; // Pending
                case 2: return 'green'; // Approved
                case 3: return 'red'; // Declined
                default: return 'grey';
            }
        },
        
        getRequestStatusIcon(statusId) {
            switch (statusId) {
                case 1: return 'mdi-clock-outline'; // Pending
                case 2: return 'mdi-check-circle'; // Approved
                case 3: return 'mdi-close-circle'; // Declined
                default: return 'mdi-help-circle';
            }
        },
        
        getStatusName(statusId) {
            switch (statusId) {
                case 1: return 'Pending';
                case 2: return 'Approved';
                case 3: return 'Declined';
                default: return 'Unknown';
            }
        },
        
        shouldShowExpandButton(note) {
            return note && note.length > 100;
        },
        
        toggleRequestNoteExpansion(requestId) {
            const keyId = String(requestId);
            this.$set(this.expandedRequestNotes, keyId, !this.expandedRequestNotes[keyId]);
        },
        
        // Edit log formatting methods
        formatFieldName(field) {
            const fieldMap = {
                'leave_count': 'Leave Count',
                'used_leaves': 'Used Leaves',
                'leave_type_id': 'Leave Type',
                'year': 'Year',
                'credit_type': 'Credit Type'
            };
            return fieldMap[field] || field;
        }
    }
};
</script>

<style lang="scss" scoped>
.leave-header {
    .filter-card {
        border-radius: 8px;
    }
}

.department-section {
    .department-card {
        border-radius: 12px;
        overflow: hidden;
    }
    
    .department-header {
        border-bottom: 1px solid #E0E0E0;
    }
}

.leave-table-header {
    display: flex;
    align-items: center;
    background-color: #FAFAFA;
    border-bottom: 1px solid #E0E0E0;
    font-weight: 600;
    font-size: 0.875rem;
    color: #616161;
}

.employee-row {
    display: flex;
    align-items: center;
    border-bottom: 1px solid #F0F0F0;
    cursor: pointer;
    transition: background-color 0.2s;
    
    &:hover {
        background-color: #FAFAFA;
    }
    
    &.expanded {
        background-color: #F8F9FA;
    }
}

.leave-col {
    &.leave-employee {
        flex: 2;
        min-width: 250px;
    }
    
    &.leave-balance {
        flex: 1;
        min-width: 120px;
    }
    
    &.leave-entitlement {
        flex: 1;
        min-width: 120px;
    }
    
    &.leave-type {
        flex: 1;
        min-width: 100px;
    }
    
    &.leave-actions {
        flex: 1;
        min-width: 150px;
        text-align: right;
    }
}

.leave-history-section {
    border-top: 1px solid #E0E0E0;
}

// Statistics cards hover effect
.v-card {
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    }
}

// Leave Requests Overview Styles
.overview-scroll-container {
    padding-right: 8px;
    
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 3px;
        
        &:hover {
            background: #a8a8a8;
        }
    }
}

.overview-request-item {
    transition: all 0.3s ease;
}

.custom-request-item {
    padding: 12px 16px !important;
    border-radius: 8px;
    background-color: #fafafa;
    transition: all 0.3s ease;
    
    &:hover {
        background-color: #f0f0f0;
    }
}

.leave-request-details {
    padding: 0 8px;
}

.duration-info {
    display: flex;
    align-items: center;
    font-weight: 600;
}

.note-section-overview {
    display: flex;
    align-items: flex-start;
    margin-top: 4px;
}

.note-content-overview {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.leave-note-overview {
    font-size: 0.8rem;
    font-weight: 400;
    font-style: italic;
    color: #666;
    line-height: 1.3;
    word-break: break-word;
    max-width: 100%;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    transition: all 0.3s ease;
    
    &.expanded {
        display: block;
        -webkit-line-clamp: unset;
        line-clamp: unset;
        overflow: visible;
    }
}

.approval-info-overview {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.leave-credits-info {
    max-width: 250px;
    
    .credits-text {
        font-size: 0.7rem;
        font-weight: 600;
        color: #666;
        display: block;
        white-space: nowrap;
    }
}

@media (max-width: 960px) {
    .approval-info-overview {
        align-items: flex-start;
        margin-top: 8px;
    }
    
    .custom-request-item {
        padding: 8px 12px !important;
    }
    
    .leave-request-details {
        padding: 0 4px;
    }
}
</style>
